import { type UseFetchOptions } from '@vueuse/core';

export type CreateFetchApiOptions = Partial<RequestInit> & {
  dedupe?: 'off' | 'inflight';
  dedupeKey?: string;
  parseError?: boolean;
  retry?: number;
  retryDelay?: number;
  retryIf?: (context: FetchRetryContext) => boolean;
  suppressConnectionIssue?: boolean;
  useAccessToken?: boolean | ((method: string | undefined, url: string) => boolean);
};

export type FetchRetryContext = {
  attempt: number;
  error?: unknown;
  maxAttempts: number;
  response?: Response;
};

export type ManagedFetchInput = URL | RequestInfo;
export type ManagedFetchInit = RequestInit | undefined;

type RetryPolicy = {
  retry: number;
  retryDelay: number;
  retryIf: (context: FetchRetryContext) => boolean;
};

const inflightRequests = new Map<string, Promise<Response>>();

export function shouldUseAccessToken(
  method: string | undefined,
  url: string,
  options?: CreateFetchApiOptions,
): boolean {
  const useAccessToken = options?.useAccessToken;
  if (typeof useAccessToken === 'function') {
    return useAccessToken(method, url);
  }
  return useAccessToken ?? false;
}

function normalizeMethod(method: string | undefined): string {
  return method?.toUpperCase() ?? 'GET';
}

function getDedupeMode(
  method: string | undefined,
  options?: CreateFetchApiOptions,
): CreateFetchApiOptions['dedupe'] {
  if (options?.dedupe) {
    return options.dedupe;
  }

  return normalizeMethod(method) === 'GET' ? 'inflight' : 'off';
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function stableSerialize(value: unknown): string {
  return (
    JSON.stringify(value, (_, entryValue) => {
      if (!isPlainObject(entryValue)) {
        return entryValue;
      }

      return Object.fromEntries(
        Object.entries(entryValue).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)),
      );
    }) ?? 'undefined'
  );
}

function getJsonStringBodyKey(body: string, headers?: HeadersInit): string {
  if (!new Headers(headers).get('Content-Type')?.includes('application/json')) {
    return body;
  }

  try {
    const parsedBody = JSON.parse(body) as unknown;

    if (isPlainObject(parsedBody) || Array.isArray(parsedBody)) {
      return stableSerialize(parsedBody);
    }
  } catch {
    return body;
  }

  return body;
}

function getRequestBodyKey(
  body: RequestInit['body'] | Record<string, unknown> | unknown[],
  headers?: HeadersInit,
  dedupeKey?: string,
): string | undefined {
  if (dedupeKey) {
    return dedupeKey;
  }

  if (body === undefined || body === null) {
    return '';
  }

  if (typeof body === 'string') {
    return getJsonStringBodyKey(body, headers);
  }

  if (body instanceof URLSearchParams) {
    return body.toString();
  }

  if (isPlainObject(body) || Array.isArray(body)) {
    return stableSerialize(body);
  }

  return undefined;
}

export function resolveRequestUrl(resource: ManagedFetchInput, baseUrl: string): string {
  let rawUrl: string;

  if (typeof resource === 'string' || resource instanceof URL) {
    rawUrl = resource.toString();
  } else {
    rawUrl = resource.url;
  }

  return new URL(rawUrl, baseUrl).toString();
}

function createInflightRequestKey(
  baseUrl: string,
  resource: ManagedFetchInput,
  options?: ManagedFetchInit,
  fetchOptions?: CreateFetchApiOptions,
): string | undefined {
  const method = normalizeMethod(
    options?.method ??
      (typeof Request !== 'undefined' && resource instanceof Request ? resource.method : undefined),
  );
  const dedupeMode = getDedupeMode(method, fetchOptions);

  if (dedupeMode !== 'inflight') {
    return undefined;
  }

  const resolvedUrl = resolveRequestUrl(resource, baseUrl);
  const bodyKey = getRequestBodyKey(options?.body, options?.headers, fetchOptions?.dedupeKey);

  if (bodyKey === undefined) {
    return undefined;
  }

  return `${method}:${resolvedUrl}:${bodyKey}`;
}

export function performFetch(
  resource: ManagedFetchInput,
  options?: ManagedFetchInit,
): Promise<Response> {
  if (resource instanceof URL) {
    return fetch(resource.toString(), options);
  }

  if (typeof Request !== 'undefined' && resource instanceof Request) {
    return fetch(resource.url, {
      method: resource.method,
      headers: resource.headers,
      body: resource.body ?? undefined,
      ...options,
    });
  }

  return fetch(resource, options);
}

async function executeWithRetry(
  executeRequest: () => Promise<Response>,
  retryPolicy: RetryPolicy,
): Promise<Response> {
  const { retry, retryDelay, retryIf } = retryPolicy;
  const maxAttempts = retry + 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await executeRequest();
      const canRetry = attempt < maxAttempts;

      if (!canRetry || !retryIf({ attempt, maxAttempts, response })) {
        return response;
      }
    } catch (error) {
      const canRetry = attempt < maxAttempts;

      if (!canRetry || !retryIf({ attempt, error, maxAttempts })) {
        throw error;
      }
    }

    if (retryDelay > 0) {
      await wait(retryDelay);
    }
  }

  throw new Error('Managed fetch retry loop exhausted unexpectedly.');
}

export function createManagedFetch(
  baseUrl: string,
  fetchOptions: CreateFetchApiOptions | undefined,
  fetchWithRetryOn401: (
    resource: ManagedFetchInput,
    options?: ManagedFetchInit,
  ) => Promise<Response>,
): UseFetchOptions['fetch'] {
  return (async (resource: ManagedFetchInput, options?: ManagedFetchInit): Promise<Response> => {
    const resolvedUrl = resolveRequestUrl(resource, baseUrl);
    const method = normalizeMethod(
      options?.method ??
        (typeof Request !== 'undefined' && resource instanceof Request
          ? resource.method
          : undefined),
    );
    const requestKey = createInflightRequestKey(baseUrl, resource, options, fetchOptions);
    const executeRequest = () =>
      shouldUseAccessToken(method, resolvedUrl, fetchOptions)
        ? fetchWithRetryOn401(resource, options)
        : performFetch(resource, options);
    const retryPolicy = resolveRetryPolicy(method, fetchOptions);
    const executeManagedRequest = () => executeWithRetry(executeRequest, retryPolicy);

    if (!requestKey) {
      return executeManagedRequest();
    }

    const inflightRequest = inflightRequests.get(requestKey);

    if (inflightRequest) {
      return inflightRequest.then((response) => response.clone());
    }

    const request = executeManagedRequest().finally(() => {
      inflightRequests.delete(requestKey);
    });

    inflightRequests.set(requestKey, request);

    return request.then((response) => response.clone());
  }) as UseFetchOptions['fetch'];
}

function resolveRetryPolicy(method: string, fetchOptions?: CreateFetchApiOptions): RetryPolicy {
  const methodDefaultRetryDelay = 300;
  const methodDefaultRetry = method === 'GET' ? 1 : 0;

  const methodDefaultRetryPredicate =
    method === 'GET' ? shouldRetryTransientFailure : shouldRetryAnyFailureOrServerError;

  const retry = fetchOptions?.retry ?? methodDefaultRetry;
  const hasExplicitRetry = fetchOptions?.retry !== undefined;

  const retryIf =
    fetchOptions?.retryIf ??
    (hasExplicitRetry ? shouldRetryAnyFailureOrServerError : methodDefaultRetryPredicate);

  return {
    retry,
    retryDelay: fetchOptions?.retryDelay ?? methodDefaultRetryDelay,
    retryIf,
  };
}

function shouldRetryAnyFailureOrServerError({ error, response }: FetchRetryContext): boolean {
  if (error) {
    return true;
  }

  if (!response) {
    return false;
  }

  return response.status >= 500;
}

const TRANSIENT_SERVER_ERROR_STATUSES = new Set([502, 503, 504]);

export function shouldRetryTransientFailure({ error, response }: FetchRetryContext): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return false;
  }

  if (error instanceof TypeError) {
    return true;
  }

  if (response && TRANSIENT_SERVER_ERROR_STATUSES.has(response.status)) {
    return true;
  }

  return false;
}

function wait(delay: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
}
